"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
	type ReactNode,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
	useTransition,
} from "react";
import {
	type ComposerKind,
	type ComposerTool,
	useComposer,
} from "@/components/editorial-system/composer/composer-context";
import {
	blocksFromBody,
	newBlockId,
	type StoryBlock,
	StoryBlocksEditor,
	storyTextLength,
} from "@/components/editorial-system/composer/story-blocks";
import { useEditorialPhonePage } from "@/components/editorial-system/editorial-phone-bar";
import { Avatar } from "@/components/editorial-system/editorial-primitives";
import {
	AUDIO_ACCEPT,
	audioFileProblem,
	titleFromFile,
	uploadTrack,
} from "@/components/editorial-system/music/upload-track";
import {
	type ComposerContext,
	createPlaylistAction,
	createPollPostAction,
	createStoryAction,
	deleteStoryDraftAction,
	getComposerContextAction,
	getStoryDraftAction,
	listStoryDraftsAction,
	publishStoryDraftAction,
	type StoryDraftSummary,
	shareTrackAction,
} from "@/lib/actions/composer";
import { uploadImageAction } from "@/lib/actions/media";
import {
	addPostEmbedAction,
	uploadPostImageAction,
} from "@/lib/actions/post-media";
import { createPost } from "@/lib/actions/posts";
import { scheduleRouterRefresh } from "@/lib/client/app-router-refresh";
import {
	deriveConversationTitle,
	plainTextToPostHtml,
} from "@/lib/posts/plain-text-body";

/*
 * One composer for taking part: a post (with images, a link or embed, a
 * poll, or your own music), and for COLORS a story or a playlist. On phones
 * it is a full-height sheet; on desktop an overlay over the page. Where it
 * goes, and what it starts as, follow the page it was opened from.
 */

interface LinkCard {
	url: string;
	provider: string;
	title?: string;
	description?: string;
	thumbnailUrl?: string;
	embedUrl?: string;
	authorName?: string;
	loading: boolean;
}

interface ImageDraft {
	id: string;
	file: File;
	preview: string;
}

/** The cover: a new file, or the one a draft was saved with. */
interface CoverDraft {
	file?: File;
	url?: string;
	preview: string;
}

const MAX_TEXT = 5000;
const MAX_IMAGES = 6;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const IMAGE_TYPES = "image/jpeg,image/png,image/gif,image/webp";
const POLL_DAYS = [
	{ days: 1, label: "1 day" },
	{ days: 3, label: "3 days" },
	{ days: 7, label: "1 week" },
];
const PROVIDERS: Record<string, string> = {
	spotify: "Spotify",
	soundcloud: "SoundCloud",
	youtube: "YouTube",
	apple_music: "Apple Music",
	"apple-music": "Apple Music",
	bandcamp: "Bandcamp",
};
const PLAYLIST_PROVIDERS = new Set([
	"spotify",
	"youtube",
	"apple_music",
	"apple-music",
]);
const EVERYWHERE = "";

function validUrl(value: string) {
	try {
		const url = new URL(value.trim());
		return url.protocol === "https:" || url.protocol === "http:";
	} catch {
		return false;
	}
}

async function unfurl(url: string): Promise<LinkCard> {
	try {
		const response = await fetch("/api/unfurl", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ url }),
		});
		const result = await response.json();
		if (result.success && result.data) {
			return {
				url: result.data.url ?? url,
				provider: result.data.provider ?? "unknown",
				title: result.data.title,
				description: result.data.description,
				thumbnailUrl: result.data.thumbnail_url,
				embedUrl: result.data.embed_url,
				authorName: result.data.author_name,
				loading: false,
			};
		}
	} catch {
		// Keep the bare link.
	}
	return { url, provider: "unknown", loading: false };
}

function cardName(card: LinkCard) {
	if (card.loading) return "Reading link…";
	if (card.title) return card.title;
	try {
		return new URL(card.url).hostname;
	} catch {
		return card.url;
	}
}

/** A link once read: thumbnail, title, where it is from. */
function LinkPreview({
	card,
	onRemove,
	disabled,
}: {
	card: LinkCard;
	onRemove: () => void;
	disabled?: boolean;
}) {
	return (
		<div className="ed-cmp-card">
			<span className="ed-cmp-card__thumb">
				{card.thumbnailUrl ? <img src={card.thumbnailUrl} alt="" /> : null}
			</span>
			<span className="ed-stack-2" style={{ flex: "1 1 auto", minWidth: 0 }}>
				<span className="ed-caption ed-cmp-card__title">{cardName(card)}</span>
				<span className="ed-meta ed-cmp-card__meta">
					{[card.authorName, PROVIDERS[card.provider] ?? "Link"]
						.filter(Boolean)
						.join(" · ")}
				</span>
			</span>
			<button
				type="button"
				className="ed-button-text ed-ui ed-cmp-card__remove"
				onClick={onRemove}
				disabled={disabled}
			>
				Remove
			</button>
		</div>
	);
}

/** A URL field that reads the link on Enter or Add. */
function LinkField({
	label,
	placeholder,
	onRead,
	disabled,
}: {
	label: string;
	placeholder: string;
	onRead: (url: string) => void;
	disabled?: boolean;
}) {
	const id = useId();
	const [value, setValue] = useState("");
	const [error, setError] = useState<string | null>(null);
	function read() {
		if (!validUrl(value)) {
			setError("Paste a full link, starting with https://");
			return;
		}
		setError(null);
		onRead(value.trim());
		setValue("");
	}
	return (
		<div className="ed-stack-2">
			<label htmlFor={id} className="ed-meta ed-secondary">
				{label}
			</label>
			<div className="ed-field">
				<input
					id={id}
					type="url"
					inputMode="url"
					autoComplete="off"
					placeholder={placeholder}
					value={value}
					disabled={disabled}
					onChange={(event) => setValue(event.target.value)}
					onPaste={(event) => {
						const pasted = event.clipboardData.getData("text").trim();
						if (validUrl(pasted)) {
							event.preventDefault();
							setError(null);
							onRead(pasted);
						}
					}}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							read();
						}
					}}
				/>
				<button
					type="button"
					className="ed-button-text ed-ui ed-link"
					onClick={read}
					disabled={disabled}
				>
					Add
				</button>
			</div>
			{error ? <span className="ed-ui ed-secondary">{error}</span> : null}
		</div>
	);
}

/** Underlined caps choices in a row (tabs, poll length, tools). */
function Choice({
	selected,
	onClick,
	children,
	disabled,
}: {
	selected: boolean;
	onClick: () => void;
	children: ReactNode;
	disabled?: boolean;
}) {
	return (
		<button
			type="button"
			aria-pressed={selected}
			className="ed-cmp-choice"
			data-selected={selected ? "true" : undefined}
			onClick={onClick}
			disabled={disabled}
		>
			{children}
		</button>
	);
}

export function EditorialComposer() {
	const composer = useComposer();
	if (!composer?.open) return null;
	return <ComposerPanel key={composer.session} />;
}

function ComposerPanel() {
	const composer = useComposer();
	const page = useEditorialPhonePage();
	const pathname = usePathname() ?? "/";
	const searchParams = useSearchParams();
	const router = useRouter();
	const titleId = useId();
	const textRef = useRef<HTMLTextAreaElement>(null);
	const fileRef = useRef<HTMLInputElement>(null);
	const coverRef = useRef<HTMLInputElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const [context, setContext] = useState<ComposerContext | null>(null);
	const [kind, setKind] = useState<ComposerKind>(
		composer?.options.kind ?? "post",
	);
	const [tool, setTool] = useState<ComposerTool>(
		composer?.options.tool ??
			(pathname.startsWith("/submissions") ? "music" : null),
	);
	const [placeId, setPlaceId] = useState<string | null>(null);
	const [scope, setScope] = useState<string | null>(null);
	const [picking, setPicking] = useState(false);
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pending, startTransition] = useTransition();

	// Post
	const [text, setText] = useState("");
	const [images, setImages] = useState<ImageDraft[]>([]);
	const [link, setLink] = useState<LinkCard | null>(null);
	const [pollOptions, setPollOptions] = useState([
		{ id: 1, label: "" },
		{ id: 2, label: "" },
	]);
	const [pollDays, setPollDays] = useState(3);
	const [trackId, setTrackId] = useState<number | null>(
		composer?.options.trackId ?? null,
	);
	const [newTrackOpen, setNewTrackOpen] = useState(false);
	const [newTrack, setNewTrack] = useState<LinkCard | null>(null);
	// A new track can be a link, or (admins and artists) an audio file.
	const [audioFile, setAudioFile] = useState<File | null>(null);
	const audioRef = useRef<HTMLInputElement>(null);
	const [newTrackTitle, setNewTrackTitle] = useState("");
	const [rights, setRights] = useState(false);
	// Story
	const [storyTitle, setStoryTitle] = useState("");
	const [storySummary, setStorySummary] = useState("");
	const [storyBlocks, setStoryBlocks] = useState<StoryBlock[]>(() => [
		{ id: newBlockId(), type: "text", text: "" },
	]);
	const [cover, setCover] = useState<CoverDraft | null>(null);
	const [draft, setDraft] = useState(false);
	// Drafts: the list, and the draft being edited.
	const [draftsOpen, setDraftsOpen] = useState(
		Boolean(composer?.options.drafts),
	);
	const [drafts, setDrafts] = useState<StoryDraftSummary[] | null>(null);
	const [draftId, setDraftId] = useState<number | null>(null);
	// A draft waiting for its delete to be confirmed, and one being worked on.
	const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
	const [busyDraft, setBusyDraft] = useState<number | null>(null);
	// Playlist
	const [playlist, setPlaylist] = useState<LinkCard | null>(null);
	const [playlistNote, setPlaylistNote] = useState("");

	const sceneHere =
		page?.place && page.place.parentHref === "/scenes" ? page.place : null;
	const sceneSlugHere = sceneHere?.href.split("/")[2] ?? null;

	// Load what the composer can offer, then set its starting point (read
	// once, when the composer opens).
	// biome-ignore lint/correctness/useExhaustiveDependencies: read once on open
	useEffect(() => {
		let live = true;
		getComposerContextAction().then((loaded) => {
			if (!live) return;
			setContext(loaded);
			const topicSlug = searchParams?.get("topic");
			const topic = topicSlug
				? loaded.topics.find((item) => item.slug === topicSlug)
				: null;
			const scenePlace = sceneSlugHere
				? loaded.places.find((place) => place.id === `scene:${sceneSlugHere}`)
				: null;
			const general = loaded.places.find(
				(place) => place.kind === "topic" && /general/i.test(place.label),
			);
			setPlaceId(
				scenePlace?.id ??
					(topic ? `topic:${topic.id}` : null) ??
					general?.id ??
					loaded.places.find((place) => place.kind === "topic")?.id ??
					loaded.places[0]?.id ??
					null,
			);
			setScope(sceneSlugHere ?? EVERYWHERE);
			if (
				!composer?.options.kind &&
				loaded.canStory &&
				(pathname.startsWith("/editorial") || pathname.startsWith("/stories"))
			) {
				setKind("story");
			}
		});
		return () => {
			live = false;
		};
	}, []);

	useEffect(() => {
		if (!draftsOpen || drafts) return;
		let live = true;
		listStoryDraftsAction().then((list) => {
			if (live) setDrafts(list);
		});
		return () => {
			live = false;
		};
	}, [draftsOpen, drafts]);

	async function publishDraft(id: number) {
		setError(null);
		setBusyDraft(id);
		const result = await publishStoryDraftAction(id);
		setBusyDraft(null);
		if (!result.success) {
			setError(result.error);
			return;
		}
		const where = result.data.sceneSlug
			? `in ${context?.scenes.find((scene) => scene.slug === result.data.sceneSlug)?.title ?? result.data.sceneSlug}`
			: "in Stories";
		finish(`Published ${where}`, `/editorial/${result.data.slug}`);
	}

	async function deleteDraft(id: number, fromEditor = false) {
		setError(null);
		setBusyDraft(id);
		const result = await deleteStoryDraftAction(id);
		setBusyDraft(null);
		setConfirmDelete(null);
		if (!result.success) {
			setError(result.error);
			return;
		}
		setDrafts(
			(current) => current?.filter((item) => item.id !== id) ?? current,
		);
		if (fromEditor || draftId === id) {
			composer?.closeComposer();
			composer?.showToast({
				message: "Draft deleted",
				action: {
					label: "Open drafts",
					onClick: () => composer.openComposer({ kind: "story", drafts: true }),
				},
			});
		}
	}

	async function openDraft(id: number) {
		setError(null);
		const loaded = await getStoryDraftAction(id);
		if (!loaded) {
			setError("Couldn’t open that draft.");
			return;
		}
		setDraftId(loaded.id);
		setStoryTitle(loaded.title);
		setStorySummary(loaded.summary);
		setStoryBlocks(blocksFromBody(loaded.body));
		setCover(
			loaded.coverUrl
				? { url: loaded.coverUrl, preview: loaded.coverUrl }
				: null,
		);
		setScope(loaded.sceneSlug ?? EVERYWHERE);
		setDraft(true);
		setKind("story");
		setDraftsOpen(false);
	}

	// Lock the page behind, focus the first field, close on Escape.
	useEffect(() => {
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") requestClose();
		};
		window.addEventListener("keydown", onKey);
		return () => {
			document.body.style.overflow = previous;
			window.removeEventListener("keydown", onKey);
		};
	});

	useEffect(() => {
		if (kind === "post") textRef.current?.focus({ preventScroll: true });
		else
			panelRef.current
				?.querySelector<HTMLElement>("[data-cmp-first]")
				?.focus({ preventScroll: true });
	}, [kind]);

	// Grow the text box with what's written.
	// biome-ignore lint/correctness/useExhaustiveDependencies: resize when the text changes
	useEffect(() => {
		const box = textRef.current;
		if (!box) return;
		box.style.height = "auto";
		box.style.height = `${Math.max(96, box.scrollHeight)}px`;
	}, [text]);

	// Free image previews.
	const previewsRef = useRef<string[]>([]);
	previewsRef.current = [
		...images.map((image) => image.preview),
		...(cover?.file ? [cover.preview] : []),
		...storyBlocks.flatMap((block) =>
			block.type === "image" && block.file ? [block.preview] : [],
		),
	];
	useEffect(
		() => () => {
			for (const url of previewsRef.current) URL.revokeObjectURL(url);
		},
		[],
	);

	const place = context?.places.find((item) => item.id === placeId) ?? null;
	const scopeLabel =
		scope === EVERYWHERE || scope === null
			? kind === "playlist"
				? "Home"
				: "Everywhere"
			: (context?.scenes.find((scene) => scene.slug === scope)?.title ?? scope);

	const dirty = Boolean(
		text.trim() ||
			images.length ||
			link ||
			trackId ||
			newTrack ||
			audioFile ||
			pollOptions.some((option) => option.label.trim()) ||
			storyTitle.trim() ||
			storySummary.trim() ||
			storyTextLength(storyBlocks) > 0 ||
			storyBlocks.some((block) => block.type === "image") ||
			cover ||
			playlist,
	);

	function requestClose() {
		if (pending) return;
		if (dirty && !confirmDiscard) {
			setConfirmDiscard(true);
			return;
		}
		composer?.closeComposer();
	}

	const filledOptions = pollOptions.filter((option) => option.label.trim());
	const ready = (() => {
		if (!context?.signedIn) return false;
		if (kind === "story" && draftsOpen) return false;
		if (kind === "story")
			return storyTitle.trim().length >= 4 && storySummary.trim().length >= 20;
		if (kind === "playlist")
			return Boolean(
				playlist &&
					!playlist.loading &&
					PLAYLIST_PROVIDERS.has(playlist.provider),
			);
		if (!place) return false;
		if (tool === "poll")
			return Boolean(text.trim()) && filledOptions.length >= 2;
		if (tool === "music")
			return Boolean(
				trackId ||
					(audioFile && rights && newTrackTitle.trim()) ||
					(newTrack && !newTrack.loading && rights),
			);
		if (link?.loading) return false;
		return Boolean(text.trim() || images.length || link);
	})();

	function addImages(files: FileList | null) {
		if (!files?.length) return;
		const room = MAX_IMAGES - images.length;
		const picked = [...files].slice(0, Math.max(0, room));
		const ok = picked.filter((file) => file.size <= MAX_IMAGE_SIZE);
		setError(
			ok.length < picked.length
				? "Images must be 10 MB or smaller."
				: files.length > room
					? `Up to ${MAX_IMAGES} images per post.`
					: null,
		);
		setImages((current) => [
			...current,
			...ok.map((file, index) => ({
				id: `${Date.now()}-${index}`,
				file,
				preview: URL.createObjectURL(file),
			})),
		]);
	}

	function finish(message: string, href?: string, linkLabel?: string) {
		composer?.showToast({ message, href, linkLabel });
		composer?.closeComposer();
		scheduleRouterRefresh();
		router.refresh();
	}

	function submit() {
		if (!ready || pending || !context) return;
		setError(null);
		startTransition(async () => {
			if (kind === "story") {
				let coverUrl: string | null = cover?.url ?? null;
				if (cover?.file) {
					const form = new FormData();
					form.append("file", cover.file);
					const uploaded = await uploadImageAction(form);
					if (!uploaded.success || !uploaded.data) {
						setError(uploaded.error ?? "Couldn’t upload the cover.");
						return;
					}
					coverUrl = uploaded.data.url;
				}
				// Inline images upload first; each becomes its own block.
				const parts: string[] = [];
				for (const block of storyBlocks) {
					if (block.type === "text") {
						if (block.text.trim()) parts.push(block.text.trim());
						continue;
					}
					const alt = (block.alt || storyTitle).replace(/[[\]\n]/g, " ").trim();
					if (block.url) {
						parts.push(`![${alt}](${block.url})`);
						continue;
					}
					if (!block.file) continue;
					const form = new FormData();
					form.append("file", block.file);
					const uploaded = await uploadImageAction(form);
					if (!uploaded.success || !uploaded.data) {
						setError(uploaded.error ?? "Couldn’t upload an image.");
						return;
					}
					parts.push(`![${alt}](${uploaded.data.url})`);
				}
				const saved = await createStoryAction({
					id: draftId,
					title: storyTitle,
					summary: storySummary,
					body: parts.join("\n\n"),
					coverUrl,
					coverAlt: storyTitle,
					sceneSlug: scope || null,
					draft,
				});
				if (!saved.success) {
					setError(saved.error);
					return;
				}
				if (saved.data.status === "draft") {
					composer?.closeComposer();
					composer?.showToast({
						message: "Draft saved",
						action: {
							label: "Open drafts",
							onClick: () =>
								composer.openComposer({ kind: "story", drafts: true }),
						},
					});
					scheduleRouterRefresh();
					return;
				} else
					finish(
						`Published ${scope ? `in ${scopeLabel}` : "in Stories"}`,
						`/editorial/${saved.data.slug}`,
					);
				return;
			}

			if (kind === "playlist" && playlist) {
				const added = await createPlaylistAction({
					url: playlist.url,
					provider: playlist.provider,
					title: playlist.title ?? "COLORS playlist",
					embedUrl: playlist.embedUrl ?? null,
					thumbnailUrl: playlist.thumbnailUrl ?? null,
					note: playlistNote,
					sceneSlug: scope || null,
				});
				if (!added.success) {
					setError(added.error);
					return;
				}
				finish(
					`Playlist added to ${scopeLabel}`,
					scope ? `/scenes/${scope}` : "/",
					"View",
				);
				return;
			}

			if (!place) return;
			const where = place.label;
			const placeHref = (postId: number) => `/posts/${postId}`;

			if (tool === "poll") {
				const created = await createPollPostAction({
					question: text,
					options: pollOptions.map((option) => option.label),
					days: pollDays,
					placeId: place.id,
				});
				if (!created.success) {
					setError(created.error);
					return;
				}
				finish(
					`Poll posted to ${where}`,
					placeHref(created.data.postId),
					"View poll",
				);
				return;
			}

			if (tool === "music") {
				// An uploaded file joins the person's music first (public,
				// since it's being shared), then the post points at it.
				let uploadedId: number | undefined;
				if (!trackId && audioFile) {
					const uploaded = await uploadTrack({
						file: audioFile,
						title: newTrackTitle,
						visibility: "public",
					});
					if (!uploaded.ok) {
						setError(uploaded.error);
						return;
					}
					uploadedId = uploaded.id;
				}
				const shared = await shareTrackAction({
					text,
					placeId: place.id,
					submissionId: trackId ?? uploadedId,
					newTrack:
						!trackId && !uploadedId && newTrack
							? {
									url: newTrack.url,
									title: newTrackTitle || newTrack.title || "Untitled",
									provider: newTrack.provider,
									thumbnailUrl: newTrack.thumbnailUrl,
									rightsAttested: rights,
								}
							: undefined,
				});
				if (!shared.success) {
					setError(shared.error);
					return;
				}
				finish(
					`Shared in ${where}`,
					placeHref(shared.data.postId),
					"View post",
				);
				return;
			}

			const [kindName, value] = place.id.split(":");
			const body = text.trim();
			const created = await createPost(
				{
					community_id: kindName === "topic" ? Number(value) : null,
					title: deriveConversationTitle(
						body,
						images.length > 0,
						Boolean(link),
					),
					body: plainTextToPostHtml(body),
				},
				kindName === "scene" ? { sceneSlug: value } : undefined,
			);
			if (!created.success) {
				setError(created.error ?? "Couldn’t post this.");
				return;
			}
			const postId = created.data.id;
			let failed = 0;
			for (const image of images) {
				const form = new FormData();
				form.append("file", image.file);
				const upload = await uploadPostImageAction(form, postId);
				if (!upload.success) failed += 1;
			}
			if (link) {
				const added = await addPostEmbedAction(postId, link.url, {
					provider: link.provider,
					title: link.title,
					description: link.description,
					thumbnail_url: link.thumbnailUrl,
					embed_url: link.embedUrl,
					author_name: link.authorName,
				});
				if (!added.success) failed += 1;
			}
			finish(
				failed
					? `Posted to ${where}, but ${failed} attachment${failed === 1 ? "" : "s"} didn’t upload`
					: `Posted to ${where}`,
				placeHref(postId),
				"View post",
			);
		});
	}

	const kinds: { key: ComposerKind; label: string }[] = [
		{ key: "post", label: "Post" },
		...(context?.canStory ? [{ key: "story" as const, label: "Story" }] : []),
		...(context?.canPlaylist
			? [{ key: "playlist" as const, label: "Playlist" }]
			: []),
	];
	const heading =
		kind === "story"
			? draftsOpen
				? "Drafts"
				: draftId
					? "Edit draft"
					: "New story"
			: kind === "playlist"
				? "Add a playlist"
				: tool === "poll"
					? "New poll"
					: tool === "music"
						? "Share your music"
						: "New post";
	const submitLabel =
		kind === "story"
			? draft
				? "Save"
				: "Publish"
			: kind === "playlist"
				? "Add"
				: "Post";

	const scopes = useMemo(
		() => [
			{ id: EVERYWHERE, label: kind === "playlist" ? "Home" : "Everywhere" },
			...(context?.scenes ?? []).map((scene) => ({
				id: scene.slug,
				label: scene.title,
			})),
		],
		[context?.scenes, kind],
	);

	const placeholder =
		tool === "poll"
			? "Ask a question"
			: tool === "music"
				? "Say something about it (optional)"
				: place?.kind === "scene"
					? `What’s happening in ${place.label}?`
					: "Start a discussion";

	return (
		<div className="ed-cmp-layer">
			<button
				type="button"
				aria-label="Close"
				tabIndex={-1}
				className="ed-cmp-veil"
				onClick={requestClose}
			/>
			<div
				ref={panelRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				className="ed-cmp-panel"
				data-kind={kind}
			>
				<span aria-hidden className="ed-cmp-grabber" />
				<div className="ed-cmp-head">
					<button
						type="button"
						className="ed-button-text ed-ui ed-cmp-head__side"
						onClick={requestClose}
						disabled={pending}
					>
						Cancel
					</button>
					<span id={titleId} className="ed-meta ed-cmp-head__title">
						{heading}
					</span>
					<button
						type="button"
						className="ed-cmp-submit"
						aria-disabled={!ready || pending}
						onClick={submit}
					>
						{pending ? "Saving…" : submitLabel}
					</button>
				</div>

				{confirmDiscard ? (
					<div className="ed-cmp-confirm ed-ui" role="alert">
						<span>Discard this?</span>
						<span className="ed-row" style={{ gap: 18 }}>
							<button
								type="button"
								className="ed-button-text ed-ui ed-link"
								onClick={() => composer?.closeComposer()}
							>
								Discard
							</button>
							<button
								type="button"
								className="ed-button-text ed-ui"
								onClick={() => setConfirmDiscard(false)}
							>
								Keep editing
							</button>
						</span>
					</div>
				) : null}

				{kinds.length > 1 ? (
					<div
						role="tablist"
						aria-label="What are you making"
						className="ed-cmp-kinds"
					>
						{kinds.map((item) => (
							<button
								key={item.key}
								type="button"
								role="tab"
								aria-selected={kind === item.key}
								className="ed-cmp-choice"
								data-selected={kind === item.key ? "true" : undefined}
								onClick={() => {
									setKind(item.key);
									setPicking(false);
									setError(null);
								}}
								disabled={pending}
							>
								{item.label}
							</button>
						))}
						{kind === "story" ? (
							<button
								type="button"
								className="ed-cmp-choice ed-cmp-kinds__drafts"
								aria-pressed={draftsOpen}
								data-selected={draftsOpen ? "true" : undefined}
								onClick={() => setDraftsOpen((value) => !value)}
								disabled={pending}
							>
								Drafts
							</button>
						) : null}
					</div>
				) : null}

				<div className="ed-cmp-body">
					{!context ? (
						<span className="ed-ui ed-secondary">Loading…</span>
					) : !context.signedIn ? (
						<span className="ed-ui ed-secondary">Log in to take part.</span>
					) : (
						<>
							<div className="ed-row" style={{ gap: 12, alignItems: "center" }}>
								<Avatar
									person={{
										name: context.me.name,
										avatarUrl: context.me.avatarUrl,
									}}
									size={32}
								/>
								<div className="ed-stack-2" style={{ minWidth: 0 }}>
									<span className="ed-ui">{context.me.name}</span>
									<button
										type="button"
										className="ed-button-text ed-meta ed-secondary ed-cmp-where"
										aria-expanded={picking}
										onClick={() => setPicking((value) => !value)}
										disabled={pending}
									>
										{kind === "post" ? "Posting in" : "Shows in"}{" "}
										<span className="ed-cmp-where__value">
											{kind === "post"
												? (place?.label ?? "Choose where")
												: scopeLabel}
										</span>{" "}
										<span aria-hidden>{picking ? "−" : "+"}</span>
									</button>
								</div>
							</div>

							{picking ? (
								<div
									className="ed-cmp-picker"
									role="listbox"
									aria-label="Where it goes"
								>
									{(kind === "post"
										? context.places.map((item) => ({
												id: item.id,
												label: item.label,
											}))
										: scopes
									).map((option) => {
										const selected =
											kind === "post"
												? option.id === placeId
												: option.id === (scope ?? EVERYWHERE);
										return (
											<button
												key={option.id || "everywhere"}
												type="button"
												role="option"
												aria-selected={selected}
												className="ed-cmp-picker__row"
												data-selected={selected ? "true" : undefined}
												onClick={() => {
													if (kind === "post") setPlaceId(option.id);
													else setScope(option.id);
													setPicking(false);
												}}
											>
												{option.label}
											</button>
										);
									})}
									{kind === "post" &&
									!context.places.some((item) => item.kind === "scene") ? (
										<span
											className="ed-ui ed-secondary"
											style={{ padding: "10px 0" }}
										>
											Join a scene to post in it.
										</span>
									) : null}
								</div>
							) : null}

							{kind === "post" ? (
								<>
									<textarea
										ref={textRef}
										aria-label={tool === "poll" ? "Question" : "Post"}
										className="ed-cmp-text"
										rows={3}
										placeholder={placeholder}
										value={text}
										maxLength={MAX_TEXT}
										onChange={(event) => setText(event.target.value)}
										disabled={pending}
									/>

									{images.length && (tool === null || tool === "link") ? (
										<ul className="ed-composer__images" aria-label="Images">
											{images.map((image, index) => (
												<li key={image.id}>
													<img
														src={image.preview}
														alt={`Attachment ${index + 1} of ${images.length}`}
													/>
													<button
														type="button"
														className="ed-button-text ed-meta"
														onClick={() =>
															setImages((current) => {
																URL.revokeObjectURL(image.preview);
																return current.filter(
																	(item) => item.id !== image.id,
																);
															})
														}
														disabled={pending}
													>
														Remove
													</button>
												</li>
											))}
										</ul>
									) : null}

									{tool === "link" ? (
										link ? (
											<LinkPreview
												card={link}
												onRemove={() => setLink(null)}
												disabled={pending}
											/>
										) : (
											<LinkField
												label="Link or embed"
												placeholder="Spotify, SoundCloud, YouTube, Apple Music or any link"
												disabled={pending}
												onRead={async (url) => {
													setLink({ url, provider: "unknown", loading: true });
													setLink(await unfurl(url));
												}}
											/>
										)
									) : null}

									{tool === "poll" ? (
										<div className="ed-stack-8" style={{ gap: 8 }}>
											<span className="ed-meta ed-secondary">Options</span>
											{pollOptions.map((option, index) => (
												<div key={option.id} className="ed-cmp-option">
													<input
														aria-label={`Option ${index + 1}`}
														placeholder={`Option ${index + 1}`}
														value={option.label}
														maxLength={80}
														disabled={pending}
														onChange={(event) =>
															setPollOptions((current) =>
																current.map((item, i) =>
																	i === index
																		? { ...item, label: event.target.value }
																		: item,
																),
															)
														}
													/>
													{pollOptions.length > 2 ? (
														<button
															type="button"
															className="ed-button-text ed-meta ed-secondary"
															onClick={() =>
																setPollOptions((current) =>
																	current.filter((_, i) => i !== index),
																)
															}
														>
															Remove
														</button>
													) : null}
												</div>
											))}
											{pollOptions.length < 4 ? (
												<button
													type="button"
													className="ed-button-text ed-ui ed-link"
													style={{ alignSelf: "flex-start", minHeight: 36 }}
													onClick={() =>
														setPollOptions((current) => [
															...current,
															{
																id:
																	Math.max(...current.map((item) => item.id)) +
																	1,
																label: "",
															},
														])
													}
												>
													Add an option
												</button>
											) : null}
											<div
												className="ed-row"
												style={{ gap: 18, alignItems: "center" }}
											>
												<span className="ed-meta ed-secondary">Open for</span>
												{POLL_DAYS.map((item) => (
													<Choice
														key={item.days}
														selected={pollDays === item.days}
														onClick={() => setPollDays(item.days)}
													>
														{item.label}
													</Choice>
												))}
											</div>
										</div>
									) : null}

									{tool === "music" ? (
										<div className="ed-stack-2">
											<span
												className="ed-meta ed-secondary"
												style={{ paddingBottom: 6 }}
											>
												Your music
											</span>
											{context.tracks.map((track) => (
												<button
													key={track.id}
													type="button"
													aria-pressed={trackId === track.id}
													className="ed-cmp-track"
													data-selected={
														trackId === track.id ? "true" : undefined
													}
													onClick={() => {
														setTrackId(trackId === track.id ? null : track.id);
														setNewTrackOpen(false);
													}}
												>
													<span className="ed-cmp-card__thumb">
														{track.cover ? (
															<img src={track.cover} alt="" />
														) : null}
													</span>
													<span
														className="ed-stack-2"
														style={{ flex: "1 1 auto", minWidth: 0 }}
													>
														<span className="ed-caption">{track.title}</span>
														<span className="ed-meta ed-secondary">
															{track.meta}
														</span>
													</span>
													<span className="ed-meta">
														{trackId === track.id ? "Selected" : ""}
													</span>
												</button>
											))}
											<button
												type="button"
												className="ed-cmp-track"
												aria-expanded={newTrackOpen}
												onClick={() => {
													setNewTrackOpen((value) => !value);
													setTrackId(null);
												}}
											>
												<span className="ed-stack-2">
													<span className="ed-ui ed-link">Add a new track</span>
													<span className="ed-ui ed-secondary">
														{context.canUploadMusic
															? "Upload audio, or paste a Spotify, SoundCloud, YouTube or Apple Music link"
															: "A Spotify, SoundCloud, YouTube or Apple Music link"}
													</span>
												</span>
											</button>
											{newTrackOpen ? (
												<div className="ed-cmp-newtrack">
													{audioFile ? (
														<>
															<div className="ed-cmp-card">
																<span className="ed-cmp-card__thumb" />
																<span
																	className="ed-stack-2"
																	style={{ flex: "1 1 auto", minWidth: 0 }}
																>
																	<span className="ed-caption ed-cmp-card__title">
																		{audioFile.name}
																	</span>
																	<span className="ed-meta ed-cmp-card__meta">
																		Audio ·{" "}
																		{(audioFile.size / 1024 / 1024).toFixed(1)}{" "}
																		MB
																	</span>
																</span>
																<button
																	type="button"
																	className="ed-button-text ed-ui ed-cmp-card__remove"
																	onClick={() => setAudioFile(null)}
																	disabled={pending}
																>
																	Remove
																</button>
															</div>
															<label className="ed-stack-2">
																<span className="ed-meta ed-secondary">
																	Track name
																</span>
																<span className="ed-field">
																	<input
																		value={newTrackTitle}
																		maxLength={180}
																		onChange={(event) =>
																			setNewTrackTitle(event.target.value)
																		}
																		disabled={pending}
																	/>
																</span>
															</label>
															<label className="ed-cmp-check ed-ui">
																<input
																	type="checkbox"
																	checked={rights}
																	onChange={(event) =>
																		setRights(event.target.checked)
																	}
																	disabled={pending}
																/>
																I made this track, or have permission to share
																it
															</label>
															<span className="ed-ui ed-secondary">
																It uploads when you post, goes into Your music
																as public, and is shared here.
															</span>
														</>
													) : null}
													{!audioFile && !newTrack && context.canUploadMusic ? (
														<div
															className="ed-row"
															style={{ gap: 14, alignItems: "center" }}
														>
															<button
																type="button"
																className="ed-pill ed-pill--solid"
																onClick={() => audioRef.current?.click()}
																disabled={pending}
															>
																Upload audio
															</button>
															<span className="ed-ui ed-secondary">
																MP3, WAV, FLAC, AIFF, M4A or OGG, up to 50 MB
															</span>
															<input
																ref={audioRef}
																type="file"
																accept={AUDIO_ACCEPT}
																hidden
																onChange={(event) => {
																	const file = event.target.files?.[0];
																	event.target.value = "";
																	if (!file) return;
																	const problem = audioFileProblem(file);
																	if (problem) {
																		setError(problem);
																		return;
																	}
																	setError(null);
																	setNewTrack(null);
																	setAudioFile(file);
																	setNewTrackTitle(titleFromFile(file.name));
																}}
															/>
														</div>
													) : null}
													{audioFile ? null : newTrack ? (
														<>
															<LinkPreview
																card={newTrack}
																onRemove={() => setNewTrack(null)}
																disabled={pending}
															/>
															<label className="ed-stack-2">
																<span className="ed-meta ed-secondary">
																	Track name
																</span>
																<span className="ed-field">
																	<input
																		value={newTrackTitle}
																		maxLength={180}
																		onChange={(event) =>
																			setNewTrackTitle(event.target.value)
																		}
																		disabled={pending}
																	/>
																</span>
															</label>
															<label className="ed-cmp-check ed-ui">
																<input
																	type="checkbox"
																	checked={rights}
																	onChange={(event) =>
																		setRights(event.target.checked)
																	}
																	disabled={pending}
																/>
																I made this track, or have permission to share
																it
															</label>
														</>
													) : (
														<LinkField
															label="Track link"
															placeholder="Paste a link to the track"
															disabled={pending}
															onRead={async (url) => {
																setNewTrack({
																	url,
																	provider: "unknown",
																	loading: true,
																});
																const card = await unfurl(url);
																setNewTrack(card);
																setNewTrackTitle(card.title ?? "");
															}}
														/>
													)}
													{audioFile ? null : (
														<span className="ed-ui ed-secondary">
															It is added to Submissions, and shared here. To
															manage your uploads, use{" "}
															<Link
																href="/your-music"
																className="ed-link"
																onClick={() => composer?.closeComposer()}
															>
																Your music
															</Link>
															.
														</span>
													)}
												</div>
											) : null}
										</div>
									) : null}
								</>
							) : null}

							{kind === "story" && draftsOpen ? (
								<div className="ed-cmp-drafts">
									{drafts === null ? (
										<span className="ed-ui ed-secondary">Loading drafts…</span>
									) : drafts.length === 0 ? (
										<span className="ed-ui ed-secondary">No drafts yet.</span>
									) : (
										drafts.map((item) => (
											<div key={item.id} className="ed-cmp-drafts__item">
												<button
													type="button"
													className="ed-cmp-drafts__row"
													data-selected={
														item.id === draftId ? "true" : undefined
													}
													onClick={() => void openDraft(item.id)}
													disabled={busyDraft !== null}
												>
													<span className="ed-caption">{item.title}</span>
													<span className="ed-meta ed-secondary">
														Edited{" "}
														{new Date(item.updatedAt).toLocaleDateString(
															"en-GB",
															{
																day: "numeric",
																month: "short",
															},
														)}
														{item.sceneSlug
															? ` · ${context.scenes.find((scene) => scene.slug === item.sceneSlug)?.title ?? item.sceneSlug}`
															: ""}
													</span>
												</button>
												<span className="ed-cmp-drafts__actions">
													{confirmDelete === item.id ? (
														<>
															<button
																type="button"
																className="ed-button-text ed-ui ed-link ed-cmp-danger"
																onClick={() => void deleteDraft(item.id)}
																disabled={busyDraft !== null}
															>
																{busyDraft === item.id
																	? "Deleting…"
																	: "Delete it"}
															</button>
															<button
																type="button"
																className="ed-button-text ed-ui ed-secondary"
																onClick={() => setConfirmDelete(null)}
																disabled={busyDraft !== null}
															>
																Keep
															</button>
														</>
													) : (
														<>
															<button
																type="button"
																className="ed-button-text ed-ui ed-link"
																onClick={() => void publishDraft(item.id)}
																disabled={busyDraft !== null}
															>
																{busyDraft === item.id
																	? "Publishing…"
																	: "Publish"}
															</button>
															<button
																type="button"
																className="ed-button-text ed-ui ed-secondary"
																onClick={() => setConfirmDelete(item.id)}
																disabled={busyDraft !== null}
															>
																Delete
															</button>
														</>
													)}
												</span>
											</div>
										))
									)}
								</div>
							) : null}

							{kind === "story" && !draftsOpen ? (
								<>
									{cover ? (
										<div className="ed-stack-2">
											<img
												src={cover.preview}
												alt=""
												className="ed-cmp-cover"
											/>
											<button
												type="button"
												className="ed-button-text ed-ui ed-secondary"
												style={{ alignSelf: "flex-start" }}
												onClick={() => {
													if (cover.file) URL.revokeObjectURL(cover.preview);
													setCover(null);
												}}
											>
												Remove cover
											</button>
										</div>
									) : (
										<button
											type="button"
											className="ed-cmp-drop"
											onClick={() => coverRef.current?.click()}
											disabled={pending}
										>
											<span className="ed-ui">Add a cover image</span>
											<span className="ed-ui ed-secondary">
												Shown full width at the top
											</span>
										</button>
									)}
									<input
										ref={coverRef}
										type="file"
										accept={IMAGE_TYPES}
										hidden
										onChange={(event) => {
											const file = event.target.files?.[0];
											event.target.value = "";
											if (!file) return;
											if (file.size > 5 * 1024 * 1024) {
												setError("Cover images must be 5 MB or smaller.");
												return;
											}
											setCover({
												file,
												preview: URL.createObjectURL(file),
											});
										}}
									/>
									<input
										data-cmp-first
										aria-label="Title"
										className="ed-cmp-title"
										placeholder="Title"
										value={storyTitle}
										maxLength={180}
										onChange={(event) => setStoryTitle(event.target.value)}
										disabled={pending}
									/>
									<textarea
										aria-label="Summary"
										className="ed-cmp-text ed-cmp-text--small"
										rows={2}
										placeholder="One or two lines for cards and the feed (at least 20 characters)"
										value={storySummary}
										maxLength={500}
										onChange={(event) => setStorySummary(event.target.value)}
										disabled={pending}
									/>
									<StoryBlocksEditor
										blocks={storyBlocks}
										onChange={setStoryBlocks}
										onError={setError}
										disabled={pending}
									/>
									<label className="ed-cmp-check ed-ui">
										<input
											type="checkbox"
											checked={draft}
											onChange={(event) => setDraft(event.target.checked)}
											disabled={pending}
										/>
										Save as a draft
									</label>
									{draftId ? (
										confirmDelete === draftId ? (
											<span className="ed-row ed-ui" style={{ gap: 18 }}>
												<span>Delete this draft for good?</span>
												<button
													type="button"
													className="ed-button-text ed-ui ed-link ed-cmp-danger"
													onClick={() => void deleteDraft(draftId, true)}
													disabled={pending || busyDraft !== null}
												>
													{busyDraft === draftId ? "Deleting…" : "Delete it"}
												</button>
												<button
													type="button"
													className="ed-button-text ed-ui ed-secondary"
													onClick={() => setConfirmDelete(null)}
												>
													Keep
												</button>
											</span>
										) : (
											<button
												type="button"
												className="ed-button-text ed-ui ed-secondary"
												style={{ alignSelf: "flex-start" }}
												onClick={() => setConfirmDelete(draftId)}
												disabled={pending}
											>
												Delete draft
											</button>
										)
									) : null}
									{!draft && storyTextLength(storyBlocks) < 100 ? (
										<span className="ed-ui ed-secondary">
											A published story needs at least 100 characters.
										</span>
									) : null}
								</>
							) : null}

							{kind === "playlist" ? (
								<>
									{playlist ? (
										<LinkPreview
											card={playlist}
											onRemove={() => setPlaylist(null)}
											disabled={pending}
										/>
									) : (
										<LinkField
											label="Playlist link"
											placeholder="Spotify, YouTube or Apple Music"
											disabled={pending}
											onRead={async (url) => {
												setPlaylist({
													url,
													provider: "unknown",
													loading: true,
												});
												setPlaylist(await unfurl(url));
											}}
										/>
									)}
									{playlist &&
									!playlist.loading &&
									!PLAYLIST_PROVIDERS.has(playlist.provider) ? (
										<span className="ed-ui ed-secondary">
											Use a Spotify, YouTube or Apple Music playlist.
										</span>
									) : null}
									<label className="ed-stack-2">
										<span className="ed-meta ed-secondary">
											A line from COLORS (optional)
										</span>
										<span className="ed-field">
											<input
												value={playlistNote}
												maxLength={280}
												placeholder="What is this playlist for?"
												onChange={(event) =>
													setPlaylistNote(event.target.value)
												}
												disabled={pending}
											/>
										</span>
									</label>
								</>
							) : null}

							{error ? (
								<output aria-live="polite" className="ed-ui ed-cmp-error">
									{error}
								</output>
							) : null}
						</>
					)}
				</div>

				{kind === "post" && context?.signedIn ? (
					<div
						role="toolbar"
						aria-label="Add to your post"
						className="ed-cmp-tools"
					>
						<Choice
							selected={false}
							onClick={() => fileRef.current?.click()}
							disabled={
								pending ||
								images.length >= MAX_IMAGES ||
								tool === "poll" ||
								tool === "music"
							}
						>
							Image
						</Choice>
						<input
							ref={fileRef}
							type="file"
							accept={IMAGE_TYPES}
							multiple
							hidden
							onChange={(event) => {
								addImages(event.target.files);
								event.target.value = "";
							}}
						/>
						{(
							[
								["link", "Link"],
								["poll", "Poll"],
								["music", "Music"],
							] as const
						).map(([key, label]) => (
							<Choice
								key={key}
								selected={tool === key}
								onClick={() => {
									setTool(tool === key ? null : key);
									setError(null);
								}}
								disabled={pending}
							>
								{label}
							</Choice>
						))}
						{MAX_TEXT - text.length < 200 ? (
							<span
								className="ed-meta ed-secondary"
								style={{ marginLeft: "auto" }}
							>
								{MAX_TEXT - text.length}
							</span>
						) : null}
					</div>
				) : null}
			</div>
		</div>
	);
}
